/**
 * The rule editor's form model (web.md §4 `RuleEditor`, engine.md §7.1): a rule's condition tree shown
 * as the prototype's two levels — groups with ALL/ANY inside and one AND/OR across them — plus actions
 * whose tag values are tag NAMES (the prototype's free "Tag" field; resolved to ids on save).
 *
 * Shared by Filters and scoring rules ("Actions on scoring" in Models, the Scoring block of a topic).
 * Field and operator vocabularies come from core (`ALLOWED_FIELDS`): a filter can never be given a
 * `folder`, `topic` or score condition.
 */

import {
  ALLOWED_FIELDS,
  type ConditionField,
  type ConditionNode,
  type FolderId,
  type GroupNode,
  type IconName,
  type ModelId,
  type QueryNode,
  type QueryUse,
  type Rule,
  type RuleAction,
  type RuleInput,
  type RuleKind,
  type SourceKind,
  type Tag,
  type TagId,
} from '@digester/core';

export type Join = 'all' | 'any';

export interface EditorGroup {
  operator: Join;
  conditions: ConditionNode[];
}

export type DraftAction =
  | { type: 'move'; folderId: string }
  | { type: 'add-tag' | 'remove-tag'; tag: string }
  | { type: 'delete' };

export interface RuleDraft {
  kind: RuleKind;
  name: string;
  icon: string;
  enabled: boolean;
  triggerFolderId: string | null;
  join: Join;
  groups: EditorGroup[];
  actions: DraftAction[];
  /** The stored tree had deeper nesting than the editor shows; saving flattens it. */
  flattened: boolean;
}

export const SOURCE_KINDS_ORDER: readonly SourceKind[] = ['rss', 'email', 'youtube', 'github', 'telegram', 'mastodon', 'bluesky', 'reddit', 'discourse', 'watcher'];

export const FIELD_LABELS: Readonly<Record<ConditionField, string>> = {
  title: 'Message · Heading',
  body: 'Message · Body',
  author: 'Message · Author',
  source: 'Source · Name',
  'source-type': 'Source · Type',
  tag: 'Message · Tag',
  read: 'Message · Read status',
  image: 'Message · Image',
  hours: 'Time in folder',
  topic: 'Message · Topic',
  'topic-score': 'Topic · Score',
  'best-score': 'Best topic score',
  folder: 'Message · Folder',
  // models.md §8.2: the writing system of title + excerpt (filters, run rules, scoring rules; not views).
  script: 'Message · Writing system',
};

/** Values of the writing-system condition, in the editor's order (kernel `detectScript`). */
export const SCRIPT_LABELS: Readonly<Record<'latin' | 'cyrillic' | 'other', string>> = {
  latin: 'Latin',
  cyrillic: 'Cyrillic',
  other: 'Other or none',
};

export const OPERATOR_LABELS: Readonly<Record<string, string>> = {
  contains: 'contains',
  'not-contains': 'does not contain',
  equals: 'equals',
  'not-equals': 'does not equal',
  'starts-with': 'starts with',
  'ends-with': 'ends with',
  in: 'is any of',
  'not-in': 'is none of',
  all: 'has all of',
  is: 'is',
  gte: 'is at least',
  gt: 'is greater than',
  lte: 'is at most',
  lt: 'is less than',
  eq: 'equals',
};

const TEXT_OPS = ['contains', 'not-contains', 'equals', 'not-equals', 'starts-with', 'ends-with'] as const;
const NUMBER_OPS = ['gte', 'gt', 'lte', 'lt', 'eq'] as const;

export const FIELD_OPERATORS: Readonly<Record<ConditionField, readonly string[]>> = {
  title: TEXT_OPS,
  author: TEXT_OPS,
  body: ['contains', 'not-contains'],
  source: ['in', 'not-in'],
  'source-type': ['in', 'not-in'],
  folder: ['in', 'not-in'],
  tag: ['in', 'not-in', 'all'],
  topic: ['in', 'not-in', 'all'],
  read: ['is'],
  image: ['is'],
  hours: NUMBER_OPS,
  'topic-score': NUMBER_OPS,
  'best-score': NUMBER_OPS,
  script: ['in', 'not-in'],
};

/**
 * The fields a rule of this kind may use, in the editor's order (the filter list starts like the
 * prototype's). 'model' = a model's run rule (models.md §8.2): folder first, no scores, no topics.
 */
export function fieldsFor(use: QueryUse): ConditionField[] {
  const order: ConditionField[] = ['folder', 'title', 'body', 'author', 'source', 'source-type', 'tag', 'read', 'image', 'script', 'hours', 'topic', 'topic-score', 'best-score'];
  const allowed = new Set(ALLOWED_FIELDS[use]);
  return order.filter((f) => allowed.has(f));
}

/** A fresh condition of `field` with sensible defaults (an incomplete text value is caught by `required`). */
/**
 * The condition a row becomes when its field is changed: the field's default condition, except that a score
 * condition keeps the model it was scored by (best score ↔ topic score), or else takes the model another score
 * condition of the same rule names (`siblings`). A rule started from a model's "Add action" names that model;
 * changing the field must not silently switch it to "the shown scores".
 */
export function conditionForField(prev: ConditionNode, field: ConditionField, siblings: readonly ConditionNode[]): ConditionNode {
  const next = defaultCondition(field);
  if (next.field !== 'best-score' && next.field !== 'topic-score') return next;
  const named = (c: ConditionNode): ModelId | undefined => ((c.field === 'best-score' || c.field === 'topic-score') && c.model ? c.model : undefined);
  const model = named(prev) ?? siblings.map(named).find((m) => m !== undefined);
  return model ? { ...next, model } : next;
}

export function defaultCondition(field: ConditionField): ConditionNode {
  switch (field) {
    case 'title':
    case 'author':
      return { type: 'condition', field, operator: 'contains', value: '' };
    case 'body':
      return { type: 'condition', field, operator: 'contains', value: '' };
    case 'source':
      return { type: 'condition', field, operator: 'in', values: [] };
    case 'source-type':
      return { type: 'condition', field, operator: 'in', values: [] };
    case 'folder':
      return { type: 'condition', field, operator: 'in', values: [] };
    case 'tag':
      return { type: 'condition', field, operator: 'in', values: [] };
    case 'topic':
      return { type: 'condition', field, operator: 'in', values: [] };
    case 'read':
      return { type: 'condition', field, operator: 'is', value: 'unread' };
    case 'image':
      return { type: 'condition', field, operator: 'is', value: 'yes' };
    case 'hours':
      return { type: 'condition', field, operator: 'gte', value: 24, unit: 'hours' };
    case 'topic-score':
      return { type: 'condition', field, operator: 'gte', value: 50 as never };
    case 'best-score':
      return { type: 'condition', field, operator: 'gte', value: 50 as never };
    case 'script':
      return { type: 'condition', field, operator: 'in', values: ['cyrillic'] };
  }
}

/** Change a condition's operator, keeping its value when the new operator accepts it. */
export function withOperator(condition: ConditionNode, operator: string): ConditionNode {
  return { ...condition, operator } as ConditionNode;
}

const isCondition = (n: QueryNode): n is ConditionNode => n.type === 'condition';

/** A stored condition tree as the editor's two levels (groups with ALL/ANY inside, one AND/OR across). */
export function groupsOf(root: GroupNode): { join: Join; groups: EditorGroup[]; flattened: boolean } {
  let flattened = false;
  let join: Join = 'all';
  let groups: EditorGroup[];
  if (root.children.length === 0) groups = [];
  else if (root.children.every(isCondition)) groups = [{ operator: root.operator, conditions: root.children.filter(isCondition) }];
  else {
    join = root.operator;
    groups = root.children.map((child) => {
      if (child.type === 'condition') return { operator: 'all' as Join, conditions: [child] };
      if (child.children.some((c) => c.type === 'group')) flattened = true;
      return { operator: child.operator, conditions: child.children.filter(isCondition) };
    });
  }
  return { join, groups, flattened };
}

/** Rule → form. Tag actions carry the tag's name (unknown ids keep the id so nothing is lost silently). */
export function toDraft(rule: Pick<Rule, 'kind' | 'name' | 'icon' | 'enabled' | 'triggerFolderId' | 'condition' | 'actions'>, tags: readonly Tag[]): RuleDraft {
  const { join, groups, flattened } = groupsOf(rule.condition);
  const tagName = (id: string): string => tags.find((t) => t.id === id)?.name ?? id;
  const actions: DraftAction[] = rule.actions.map((a) =>
    a.type === 'move' ? { type: 'move', folderId: a.folderId } : a.type === 'delete' ? { type: 'delete' } : { type: a.type, tag: tagName(a.tagId) },
  );
  return { kind: rule.kind, name: rule.name, icon: rule.icon, enabled: rule.enabled, triggerFolderId: rule.triggerFolderId, join, groups, actions, flattened };
}

/** Form → condition tree: one group is stored flat (like the seeded ladder), several as nested groups. */
export function conditionOf(draft: Pick<RuleDraft, 'groups' | 'join'>): GroupNode {
  const [only] = draft.groups;
  if (draft.groups.length === 0) return { type: 'group', operator: 'all', children: [] };
  if (draft.groups.length === 1 && only) return { type: 'group', operator: only.operator, children: only.conditions };
  return { type: 'group', operator: draft.join, children: draft.groups.map((g) => ({ type: 'group', operator: g.operator, children: g.conditions })) };
}

/** Resolve tag names to ids. `ensureTag` creates a tag (the engine returns an existing one with the same name). */
export async function actionsOf(actions: readonly DraftAction[], tags: readonly Tag[], ensureTag: (name: string) => Promise<TagId>): Promise<RuleAction[]> {
  const out: RuleAction[] = [];
  for (const a of actions) {
    if (a.type === 'move') out.push({ type: 'move', folderId: a.folderId as FolderId });
    else if (a.type === 'delete') out.push({ type: 'delete' });
    else {
      const name = a.tag.trim();
      const known = tags.find((t) => t.id === name || t.name.toLowerCase() === name.toLowerCase());
      out.push({ type: a.type, tagId: known ? known.id : await ensureTag(name) });
    }
  }
  return out;
}

/** The engine input for a draft (tags already resolved). */
export function inputOf(draft: RuleDraft, actions: RuleAction[]): RuleInput {
  return {
    kind: draft.kind,
    name: draft.name.trim(),
    icon: draft.icon as IconName,
    enabled: draft.enabled,
    triggerFolderId: draft.kind === 'filter' ? (draft.triggerFolderId as FolderId | null) : null,
    condition: conditionOf(draft),
    actions,
  };
}

/** The models a rule's score conditions name, in order of appearance (models.md §8.1); [] = none named. */
export function modelsOfCondition(root: GroupNode): string[] {
  const out: string[] = [];
  const visit = (n: QueryNode): void => {
    if (n.type === 'group') n.children.forEach(visit);
    else if ((n.field === 'best-score' || n.field === 'topic-score') && n.model && !out.includes(n.model)) out.push(n.model);
  };
  visit(root);
  return out;
}

/** True when a condition tree tests scores without naming a model (it acts on the shown scores). */
export function testsShownScores(root: GroupNode): boolean {
  const visit = (n: QueryNode): boolean => (n.type === 'group' ? n.children.some(visit) : (n.field === 'best-score' || n.field === 'topic-score') && !n.model);
  return visit(root);
}

/** "2 groups · 1 action" (the prototype's filter subtitle). */
export function ruleSummary(rule: Pick<Rule, 'condition' | 'actions'>): string {
  const root = rule.condition;
  const groups = root.children.length === 0 ? 0 : root.children.every(isCondition) ? 1 : root.children.length;
  const g = groups === 0 ? 'Otherwise' : `${groups} group${groups === 1 ? '' : 's'}`;
  return `${g} · ${rule.actions.length} action${rule.actions.length === 1 ? '' : 's'}`;
}

/**
 * A new, empty draft of a kind. A scoring rule's first condition is `best-score ≥ 50` of `model` (the
 * model whose "Add action" created it, or the first enabled model): every seeded rule names its model,
 * and a condition without one acts on whatever scores are shown (models.md §8.1).
 */
export function newRuleDraft(kind: RuleKind, triggerFolderId: string | null, model: string | null = null): RuleDraft {
  const first = defaultCondition(kind === 'filter' ? 'title' : 'best-score');
  const condition = model && first.field === 'best-score' ? ({ ...first, model: model as ModelId } as ConditionNode) : first;
  return {
    kind,
    name: kind === 'filter' ? 'New rule' : 'New scoring rule',
    icon: kind === 'filter' ? 'list-filter' : 'gauge',
    enabled: true,
    triggerFolderId: kind === 'filter' ? triggerFolderId : null,
    join: 'all',
    groups: [{ operator: 'all', conditions: [condition] }],
    actions: [{ type: 'move', folderId: 'priority' }],
    flattened: false,
  };
}
